import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  ClipboardList,
  ConciergeBell,
  Timer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AnalyticsExportMenu } from "../shared/AnalyticsExportMenu";
import { MetricCard } from "../shared";
import { METRIC_CARD_ICON_STROKE_WIDTH } from "../shared/constants";
import {
  REVENUE_CHART_CARD_SHADOW,
  TOP_SERVICES_CARD_BORDER,
  TOP_SERVICES_CARD_GAP,
  TOP_SERVICES_CARD_ICON_COLOR,
  TOP_SERVICES_CARD_MIN_HEIGHT,
  TOP_SERVICES_CARD_PADDING_BOTTOM,
  TOP_SERVICES_CARD_PADDING_TOP,
  TOP_SERVICES_CARD_PADDING_X,
  TOP_SERVICES_CARD_RADIUS,
  TOP_SERVICES_SERVICE_CELL_WIDTH,
  TOP_SERVICES_TABLE_ROW_HEIGHT,
} from "../services/constants";
import {
  AvgDelayCell,
  LatePercentCell,
  LateRequestsCell,
  UnderestimatedRateBadge,
  UnderestimatedServiceNameCell,
} from "./operationsUnderestimatedTableCells";
import type {
  OperationsUnderestimatedServiceRow,
  OperationsUnderestimatedSummary,
} from "./types";

const TABLE_LABEL_CLASS =
  "h-10 border-b border-[#E5E5E5] p-2 text-center align-middle text-[10px] font-medium leading-5 text-[#737373]";

const TABLE_BODY_ROW_STYLE = {
  height: TOP_SERVICES_TABLE_ROW_HEIGHT,
  maxHeight: TOP_SERVICES_TABLE_ROW_HEIGHT,
} as const;

const TABLE_BODY_CELL_STYLE = {
  ...TABLE_BODY_ROW_STYLE,
  boxSizing: "border-box" as const,
};

const TABLE_BODY_CELL_CLASS =
  "box-border overflow-hidden border-b border-[#E5E5E5] p-2 text-center align-middle";

function bodyCellClass(isLastRow: boolean, extra?: string) {
  return cn(TABLE_BODY_CELL_CLASS, isLastRow && "border-b-0", extra);
}

interface OperationsUnderestimatedServicesCardProps {
  summary: OperationsUnderestimatedSummary;
  rows: OperationsUnderestimatedServiceRow[];
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
  className?: string;
  onViewAll?: () => void;
}

export const OperationsUnderestimatedServicesCard = memo(
  function OperationsUnderestimatedServicesCard({
    summary,
    rows,
    onExport,
    exportDisabled = false,
    className,
    onViewAll,
  }: OperationsUnderestimatedServicesCardProps) {
    const { t } = useTranslation();

    return (
      <div
        className={cn(
          "flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-white font-sans",
          REVENUE_CHART_CARD_SHADOW,
          className,
        )}
        style={{
          maxWidth: "100%",
          minHeight: TOP_SERVICES_CARD_MIN_HEIGHT,
          borderRadius: TOP_SERVICES_CARD_RADIUS,
          border: `1px solid ${TOP_SERVICES_CARD_BORDER}`,
          paddingTop: TOP_SERVICES_CARD_PADDING_TOP,
          paddingRight: TOP_SERVICES_CARD_PADDING_X,
          paddingBottom: TOP_SERVICES_CARD_PADDING_BOTTOM,
          paddingLeft: TOP_SERVICES_CARD_PADDING_X,
          gap: TOP_SERVICES_CARD_GAP,
        }}
      >
        <div className="flex shrink-0 items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              className="flex shrink-0 items-center justify-center"
              style={{
                color: TOP_SERVICES_CARD_ICON_COLOR,
                width: 13.34,
                height: 13.33,
              }}
            >
              <ConciergeBell
                className="size-[13.34px] shrink-0"
                strokeWidth={METRIC_CARD_ICON_STROKE_WIDTH}
              />
            </span>
            <h3 className="text-sm font-semibold leading-normal text-[#171717]">
              {t(
                "analytics.operations.underestimated.title",
                "Under estimated services",
              )}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {onExport ? (
              <AnalyticsExportMenu
                onExport={onExport}
                disabled={exportDisabled}
              />
            ) : null}
            {onViewAll ? (
              <Button
                type="button"
                variant="span"
                className="inline-flex h-auto items-center gap-0.5 p-0 text-xs font-medium text-[#171717] hover:bg-transparent"
                onClick={onViewAll}
              >
                {t("analytics.common.viewAll", "View all")}
                <ChevronRight className="size-3.5" />
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-3">
          <MetricCard
            title={t(
              "analytics.operations.underestimated.totalRequests",
              "Total requests",
            )}
            value={String(summary.totalRequests)}
            icon={<ConciergeBell />}
            width="fill"
            className="!min-h-[108px] !p-4"
          />
          <MetricCard
            title={t("analytics.operations.underestimated.onTime", "On-time")}
            value={`${summary.onTimePercent}%`}
            icon={<ClipboardList />}
            width="fill"
            className="!min-h-[108px] !p-4"
          />
          <MetricCard
            title={t(
              "analytics.operations.underestimated.avgDelay",
              "Avg. delay",
            )}
            value={summary.avgDelay}
            icon={<Timer />}
            width="fill"
            className="!min-h-[108px] !p-4 [&>p:nth-of-type(2)]:text-red-600"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-sm">
            <thead>
              <tr>
                <th className={cn(TABLE_LABEL_CLASS, "w-9")}>
                  {t("analytics.common.rate", "Rate")}
                </th>
                <th
                  className={TABLE_LABEL_CLASS}
                  style={{
                    width: TOP_SERVICES_SERVICE_CELL_WIDTH,
                    maxWidth: TOP_SERVICES_SERVICE_CELL_WIDTH,
                  }}
                >
                  {t("analytics.operations.underestimated.service", "Service")}
                </th>
                <th className={TABLE_LABEL_CLASS}>
                  {t(
                    "analytics.operations.underestimated.percentLate",
                    "% Late",
                  )}
                </th>
                <th className={TABLE_LABEL_CLASS}>
                  {t(
                    "analytics.operations.underestimated.avgDelayTime",
                    "Avg. delay time",
                  )}
                </th>
                <th className={TABLE_LABEL_CLASS}>
                  {t(
                    "analytics.operations.underestimated.lateRequests",
                    "Late requests",
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const isLastRow = index === rows.length - 1;

                return (
                  <tr key={row.id} style={TABLE_BODY_ROW_STYLE}>
                    <td
                      className={bodyCellClass(isLastRow, "w-9")}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <div className="flex h-full items-center justify-center">
                        <UnderestimatedRateBadge rate={row.rate} />
                      </div>
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={{
                        ...TABLE_BODY_CELL_STYLE,
                        width: TOP_SERVICES_SERVICE_CELL_WIDTH,
                        maxWidth: TOP_SERVICES_SERVICE_CELL_WIDTH,
                        verticalAlign: "middle",
                      }}
                    >
                      <UnderestimatedServiceNameCell row={row} />
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <LatePercentCell percent={row.latePercent} />
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <AvgDelayCell delay={row.avgDelayTime} />
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <LateRequestsCell
                        late={row.lateRequests}
                        total={row.totalRequests}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);
